import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
  Loader,
  SelectPicker,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { CommercialScheduleServices } from "../../../core/services/CommercialScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import {
  CommercialFields,
  CommercialListParams,
} from "../Commercials/CommercialDataTypes";
import { Permission } from "../../../core/types/PermissionInterface";
import "./CommercialSchedules.css";
import {
  isEmptyVal,
  isNotEmpty,
  displayField,
  getTodayShortDay,
  formatTimeField,
} from "../../../utils/helpers";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import {
  messageTypeArr,
  appConstObj,
  roleTypeArr,
  weekDaysObjArr,
  fullDayHourObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import MediaPlayerFormatter from "../../components/Elements/MediaPlayerFormatter";
import CommercialScheduleItemBulkAction from "./CommercialScheduleItemBulkAction";
import { updateIn } from "json-immut";
import CommercialScheduleItemTimeCell from "../../components/Elements/CommercialScheduleItemTimeCell";
import CommercialInfoCell from "../../components/Elements/CommercialInfoCell";
import { useMediaContext } from "../../../context/MediaContext";
import AudioPlayer from "../../components/Elements/AudioPlayer";

function CommercialScheduleItems({
  commercialScheduleId,
  withHeader = true,
  enableDayFilter = true,
  onDeleteItems,
  onSetTotalItems,
  enableBulkAction = [],
  itemSearchParams = {
    smart_search: "",
    play_for_day: getTodayShortDay().toLowerCase(),
    start_hour: "",
  },
  ...props
}: {
  commercialScheduleId: number;
  withHeader?: boolean;
  enableDayFilter?: boolean;
  onDeleteItems?: Function;
  onSetTotalItems?: Function;
  enableBulkAction?: string[];
  itemSearchParams?: any;
}) {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [commercialScheduleInfo, setCommercialScheduleInfo] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(true);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<CommercialFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const [smartSearchInput, setSmartSearch] = useState<string>(
    itemSearchParams.smart_search
  );
  const [playForDaySearch, setPlayForDaySearch] = useState<string>(
    itemSearchParams.play_for_day
  );
  const [startHourSearch, setStartHourSearch] = useState<string>(
    itemSearchParams.start_hour
  );
  const loggedInUserDetail = UtilityServices.getLoggedInUserDetails();
  const { playingMediaInfo, setPlayingMediaInfo, isPlaying, setIsPlaying } =
    useMediaContext();
  const initSearchFormData = {
    smart_search: itemSearchParams.smart_search,
    play_for_day: itemSearchParams.play_for_day,
  };

  const [formData, setFormData] =
    useState<CommercialListParams>(initSearchFormData);
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = useState<any[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys?.length === data?.length) {
    checked = true;
  } else if (checkedKeys?.length === 0) {
    checked = false;
  } else if (checkedKeys?.length > 0 && checkedKeys?.length < data?.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.item_id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  async function getCommercialScheduleItems(params: CommercialListParams) {
    try {
      if (isEmptyVal(commercialScheduleId)) return;
      setLoading(true);
      const res = await CommercialScheduleServices.getCommercialScheduleItems(
        commercialScheduleId,
        params
      );
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getViewCommercialScheduleDetail(id: number) {
    try {
      setLoadingSchedule(true);
      const res = await CommercialScheduleServices.viewCommercialSchedule(id);

      setCommercialScheduleInfo({ ...res?.data?.data });
      setRows(res.data.data.total_items);
      getCommercialScheduleItems(
        getSearchParams({
          page: page,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: res.data.data.total_items,
        })
      );
      setLoadingSchedule(false);
    } catch (error) {
      setLoadingSchedule(false);
    }
  }

  const handleSearchFormChange = (data: any) => {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  };

  async function init() {
    await getViewCommercialScheduleDetail(commercialScheduleId);
  }

  useEffect(() => {
    init();
  }, [commercialScheduleId]);

  async function bulkDeleteAction() {
    try {
      let params = { item_ids: checkedKeys.join() };
      let res =
        await CommercialScheduleServices.deleteItemsFromCommercialSchedule(
          commercialScheduleId,
          params
        );
      UtilityServices.showToaster({
        message: res?.data?.message,
      });

      onSetTotalItems && onSetTotalItems(res?.data?.data?.total_items);
      onDeleteItems &&
        onDeleteItems(commercialScheduleId, {
          smart_search: smartSearchInput,
          play_for_day: playForDaySearch,
          start_hour: startHourSearch,
        });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ start_hour: startHourSearch },
      ...{ play_for_day: playForDaySearch },
      ...{ sort_by, sort_order },
      ...{ rows },
      ...data,
    };
    let params: CommercialListParams = omitBy(isEmptyVal, paramsData);
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getCommercialScheduleItems(
      getSearchParams({
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      })
    );
  }

  const filterCommercialScheduleItems = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getCommercialScheduleItems(
        getSearchParams({ smart_search: smartSearchInput, rows: rows })
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setStartHourSearch("");
    if (enableDayFilter) setPlayForDaySearch(getTodayShortDay().toLowerCase());
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    getCommercialScheduleItems(
      getSearchParams({
        smart_search: "",
        start_hour: "",
        play_for_day: playForDaySearch,
        page: 1,
        sort_by: null,
        sort_order: null,
      })
    );
  };

  const deletePermission = UtilityServices.hasPermission("delete-item");
  const permissionObj: Permission = {
    delete: deletePermission,
  };

  return (
    <div className="commercial-schedule-items">
      {loadingSchedule && (
        <Loader
          style={{ zIndex: 9999 }}
          size="lg"
          content="Loading..."
          center
          backdrop={true}
        />
      )}
      <Container className="container">
        {withHeader && (
          <Header className="h4">
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <h4>
                  {t("COMMERCIAL_SCHEDULE")}{" "}
                  {commercialScheduleInfo?.schedule_title} {t("ITEMS")}
                </h4>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </Header>
        )}

        <Content className="commercial-schedule-items">
          {UtilityServices.hasLoggedInUserType(roleTypeArr.backend) && (
            <>
              <div className="row commercial-schedule-item-controls-row">
                <CommercialScheduleItemBulkAction
                  commercialScheduleData={commercialScheduleInfo}
                  checkedKeys={checkedKeys}
                  setCheckedKeys={setCheckedKeys}
                  onDeleteItems={bulkDeleteAction}
                  {...(enableBulkAction.length > 0 && { enableBulkAction })}
                />
                <div className="commercial-schedule-item-search">
                  <div className="commercial-schedule-item-smart-search-wrap">
                    <InputGroup className="commercial-schedule-item-smart-search">
                    {commercialScheduleInfo?.new_schedule_everyday &&
                      enableDayFilter &&
                      commercialScheduleInfo?.new_schedule_everyday.toString() ===
                        "1" && (
                        <SelectPicker
                          data={weekDaysObjArr}
                          valueKey="value"
                          labelKey="label"
                          onSelect={(value, item, e) => {
                            handleSearchFormChange({
                              path: "play_for_day",
                              value,
                            });
                            setPlayForDaySearch(value);
                            getCommercialScheduleItems(
                              getSearchParams({
                                play_for_day: value,
                              })
                            );
                          }}
                          onClean={(e: any) => {
                            handleSearchFormChange({
                              path: "play_for_day",
                              value: getTodayShortDay().toLowerCase(),
                            });
                            setPlayForDaySearch(
                              getTodayShortDay().toLowerCase()
                            );
                            getCommercialScheduleItems(
                              getSearchParams({
                                play_for_day: getTodayShortDay().toLowerCase(),
                              })
                            );
                          }}
                          name="play_for_day"
                          value={playForDaySearch}
                          placeholder={t("DAY")}
                        />
                      )}

                    <SelectPicker
                      data={fullDayHourObjArr}
                      valueKey="value"
                      labelKey="label"
                      onSelect={(value, item, e) => {
                        handleSearchFormChange({ path: "start_hour", value });
                        setStartHourSearch(value);
                        getCommercialScheduleItems(
                          getSearchParams({ start_hour: value })
                        );
                      }}
                      onClean={(e: any) => {
                        handleSearchFormChange({
                          path: "start_hour",
                          value: "",
                        });
                        setStartHourSearch("");
                        getCommercialScheduleItems(
                          getSearchParams({ start_hour: "" })
                        );
                      }}
                      name="start_hour"
                      value={startHourSearch}
                      placeholder={t("START") + " " + t("HOUR")}
                    />
                    <Input
                      className="form-control"
                      placeholder={t("SMART_SEARCH_BY", {
                        entity:
                          t("ITEM").toLowerCase() +
                          " " +
                          t("NAME").toLowerCase() +
                          ", " +
                          t("BRAND").toLowerCase() +
                          ", " +
                          t("OR").toLowerCase() +
                          " " +
                          t("CAMPAIGN").toLowerCase(),
                      })}
                      value={smartSearchInput}
                      onChange={(val: any) => setSmartSearch(val)}
                      onKeyDown={(e: any) => filterCommercialScheduleItems(e)}
                    />
                    <InputGroup.Button
                      title={t("SEARCH")}
                      onClick={(e: any) => filterCommercialScheduleItems(e)}
                    >
                      <SearchIcon />
                    </InputGroup.Button>
                    <InputGroup.Button
                      title={t("CLEAR")}
                      onClick={() => clearSmartSearch()}
                    >
                      <CloseIcon />
                    </InputGroup.Button>
                    </InputGroup>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className={total > 0 ? "commercial-table" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
              renderEmpty={() => (
                <div style={{ textAlign: "center", padding: "20px" }}>
                  <h4>
                    {t("NO_ENTITY_FOUND_FOR", {
                      entity: t("ITEM"),
                      for: commercialScheduleInfo?.schedule_title,
                    })}
                  </h4>
                </div>
              )}
            >
              <Column width={40} align="center" fixed>
                <CustomHeaderCell>
                  <div>
                    <Checkbox
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                    />
                  </div>
                </CustomHeaderCell>
                <CheckCell
                  dataKey="item_id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column width={60} align="center" fixed>
                <CustomHeaderCell>{t("PLAY")}</CustomHeaderCell>
                <MediaPlayerFormatter
                  rowData={(rowData: any) => rowData}
                  mediaUrlKey={"media_url"}
                  mediaImageKey={"media_image"}
                  uniqueId={"item_id"}
                />
              </Column>
              <Column width={100} sortable resizable flexGrow={1}>
                <CustomHeaderCell>{t("NAME")}</CustomHeaderCell>
                <CommercialInfoCell
                  rowData={(rowData: any) => rowData}
                  dataKey="title"
                  showData={[
                    "campaign_name",
                    "commercial_use_for",
                    "media_duration",
                  ]}
                />
              </Column>
              <Column width={80} sortable resizable>
                <CustomHeaderCell>{t("TYPE")}</CustomHeaderCell>
                <CustomCell dataKey="media_nature">
                  {(rowData: any) => (
                    <span
                      title={
                        rowData?.commercial_use_for + " " + rowData.media_nature
                      }
                    >
                      {displayField(rowData.media_nature)} (
                      {rowData.commercial_use_for == "external"
                        ? t("Ext")
                        : t("Int")}
                      )
                    </span>
                  )}
                </CustomCell>
              </Column>
              <Column width={100} sortable resizable>
                <CustomHeaderCell>
                  {t("STARTS")} {t("AT")}
                </CustomHeaderCell>
                <CommercialScheduleItemTimeCell
                  dataKey="play_starts_at"
                  rowData={(rowData: any) => rowData}
                />
              </Column>
              <Column width={120} sortable resizable>
                <CustomHeaderCell>{t("BRAND")}</CustomHeaderCell>
                <CustomCell dataKey="brand_name">
                  {(rowData: any) => (
                    <span title={`${rowData.brand_name}`}>
                      {displayField(rowData.brand_name)}
                    </span>
                  )}
                </CustomCell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total"]}
            total={total}
          />
        </Footer>
        <AudioPlayer />
        <ToastContainer />
      </Container>
    </div>
  );
}

export default CommercialScheduleItems;
